import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BaseMessage } from "../../interfaces/messages.type";

export interface MessageData {
  conversation_id?: string;
  clientId?: string;
  messages: BaseMessage[];
}

export interface DisplayMessage extends BaseMessage {
  legs: BaseMessage[];
}

export const groupMessagesForDisplay = (
  messages: BaseMessage[]
): DisplayMessage[] => {
  const grouped = new Map<string, DisplayMessage>();

  messages.forEach((message, index) => {
    const key =
      message.send_id ||
      message.clientId ||
      message.id ||
      `unkeyed-${message.timestamp}-${index}`;
    const existing = grouped.get(key);

    if (existing) {
      existing.legs.push(message);
      if (!existing.conversation_id && message.conversation_id) {
        existing.conversation_id = message.conversation_id;
      }
      if (!existing.send_id && message.send_id) {
        existing.send_id = message.send_id;
      }
      return;
    }

    grouped.set(key, { ...message, legs: [message] });
  });

  return Array.from(grouped.values());
};

export interface MessagesState {
  data: MessageData[];
}

const initialState: MessagesState = {
  data: [],
};

export const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    setMessages: (state, action: PayloadAction<BaseMessage[]>) => {
      action.payload.forEach((message) => {
        if (!message.conversation_id) return;
        const index = state.data.findIndex(
          (item) => item.conversation_id === message.conversation_id
        );
        if (index !== -1) {
          if (
            message.id &&
            state.data[index].messages.some((m) => m.id === message.id)
          ) return;
          state.data[index].messages.push(message);
        } else {
          state.data.push({
            conversation_id: message.conversation_id,
            messages: [message],
          });
        }
      });
    },
    updateDeliveryStatusSlice: (
      state,
      action: PayloadAction<{
        error?: string | null;
        display_error?: string | null;
        id: string;
        status: string;
      }>
    ) => {
      const index = state.data.findIndex((item) => {
        return item.messages.some((m) => m.id === action.payload.id);
      });

      if (index !== -1) {
        const messageIndex = state.data[index].messages.findIndex((m) => m.id === action.payload.id);
        const displayError = action.payload.display_error ?? action.payload.error ?? null;
        state.data[index].messages[messageIndex] = {
          ...state.data[index].messages[messageIndex],
          status: action.payload.status,
          failed_reason: displayError,
          display_error: displayError,
        };
      }
    },
    onMessage: (state, action: PayloadAction<BaseMessage>) => {
      const placeholderIndex = action.payload.clientId
        ? state.data.findIndex((item) =>
            item.messages.some(
              (message) =>
                message.clientId === action.payload.clientId && !message.id
            )
          )
        : -1;

      if (placeholderIndex !== -1) {
        state.data[placeholderIndex].messages = state.data[
          placeholderIndex
        ].messages.filter(
          (message) =>
            message.clientId !== action.payload.clientId || Boolean(message.id)
        );
        if (state.data[placeholderIndex].messages.length === 0) {
          state.data.splice(placeholderIndex, 1);
        }
      }

      const index = action.payload.conversation_id
        ? state.data.findIndex(
            (item) =>
              item.conversation_id === action.payload.conversation_id
          )
        : state.data.findIndex(
            (item) =>
              item.clientId === action.payload.clientId &&
              !item.conversation_id
          );

      if (index === -1) {
        state.data.push({
          conversation_id: action.payload.conversation_id,
          clientId: action.payload.conversation_id
            ? undefined
            : action.payload.clientId,
          messages: [action.payload],
        });
        return;
      }

      if (
        action.payload.id &&
        state.data[index].messages.some(
          (message) => message.id === action.payload.id
        )
      ) {
        return;
      }

      state.data[index].messages.push(action.payload);
    },

    deleteMessage: (state, action: PayloadAction<string>) => {
      const index = state.data.findIndex((item) => {
        return item.messages.some((m) => m.id === action.payload);
      });

      if (index !== -1) {
        const messageIndex = state.data[index].messages.findIndex((m) => m.id === action.payload);
        state.data[index].messages[messageIndex] = { ...state.data[index].messages[messageIndex], deleted: true };
      }
    },
  },
});

export const { setMessages, onMessage, deleteMessage, updateDeliveryStatusSlice } = messagesSlice.actions;

export default messagesSlice.reducer;
